import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Component,
  inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  MatSelectionList,
  MatSelectionListChange,
} from '@angular/material/list';
import { SideDialogRef } from '../../../ecaps-core/side-dialog/side-dialog-ref.class';
import { SIDE_DIALOG_DATA } from '../../../ecaps-core/side-dialog/side-dialog.injectors';
import { GoogleAnalyticsService } from '../../../google/services/google-analytics.service';
import { IGridColumn } from '../../grids/models/grid-column.interface';
import { DialogOptions } from './models/dialog-options.model';

interface ISelectionColumn extends IGridColumn {
  selected: boolean;
}

interface IColumnOrderItem {
  key: string;
  text: string;
}

@Component({
  selector: 'customize-grid-dialog',
  templateUrl: './customize-grid-dialog.component.html',
  styleUrls: ['./customize-grid-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CustomizeGridDialogComponent implements OnInit {
  @ViewChild('visibleColumnsList') visibleColumnsList: MatSelectionList;

  columnList: ISelectionColumn[] = [];
  columnOrder: IColumnOrderItem[] = [];

  private analytics = inject(GoogleAnalyticsService);

  protected dialogData: DialogOptions = inject(SIDE_DIALOG_DATA);

  constructor(
    protected dialogRef: SideDialogRef<string[], CustomizeGridDialogComponent>
  ) {}

  ngOnInit() {
    this.columnOrder = this.dialogData.visibleColumns
      .filter((key) => !['rank', 'compare', 'name'].includes(key))
      .map((key) => {
        return {
          key,
          text:
            this.dialogData.columnList.find((item) => item.key === key)?.text ||
            '',
        } as IColumnOrderItem;
      });

    this.columnList = [
      ...this.dialogData.columnList
        .sort((a: IGridColumn, b: IGridColumn) => a.text.localeCompare(b.text))
        .map((item) => {
          return {
            ...item,
            selected: this.dialogData.visibleColumns.indexOf(item.key) > -1,
          } as ISelectionColumn;
        }),
    ];
  }

  onSelectionChange(event: MatSelectionListChange) {
    const newOrder = this.visibleColumnsList.selectedOptions.selected.map(
      (item) => {
        return {
          key: (item.value as ISelectionColumn).key,
          text: (item.value as ISelectionColumn).text,
        } as IColumnOrderItem;
      }
    );

    this.matchSortOrder(newOrder, this.columnOrder, 'key');

    this.columnOrder = newOrder;
  }

  private matchSortOrder<T>(array: T[], order: T[], key?: string) {
    array.sort((a, b) => {
      const aIndex = order.findIndex(
        (element) => (!!key ? element[key] : element) === (!!key ? a[key] : a)
      );
      const bIndex = order.findIndex(
        (element) => (!!key ? element[key] : element) === (!!key ? b[key] : b)
      );

      if (aIndex === -1 && bIndex === -1) {
        return 0;
      } else if (aIndex === -1) {
        return 1;
      } else if (bIndex === -1) {
        return -1;
      } else {
        return aIndex - bIndex;
      }
    });
  }

  columnDropped(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.columnOrder, event.previousIndex, event.currentIndex);
  }

  okClick() {
    const columnAdditions = this.visibleColumnsList.selectedOptions.selected
      .map((item) => (item.value as ISelectionColumn).key)
      .filter((key) => this.dialogData.visibleColumns.indexOf(key) === -1)
      .filter((key) => ['budgetPrice'].includes(key));

    if (columnAdditions.length > 0) {
      this.analytics.viewItemList(
        columnAdditions.map((key) => ({
          item_variant: key,
        })),
        'Selection Grid',
        this.dialogData.modelGroup
      );
    }

    this.dialogRef.close(this.columnOrder.map((item) => item.key));
  }

  resetClick() {
    this.visibleColumnsList.selectedOptions.setSelection(
      ...this.visibleColumnsList.options.filter((item) =>
        this.dialogData.visibleColumnsReset.includes(
          (item.value as ISelectionColumn).key
        )
      )
    );

    const newOrder = this.columnList
      .filter((column) =>
        this.dialogData.visibleColumnsReset.includes(column.key)
      )
      .map((column) => {
        return {
          key: column.key,
          text: column.text,
        } as IColumnOrderItem;
      });

    this.matchSortOrder(
      newOrder,
      this.dialogData.visibleColumnsReset.map((key) => {
        return {
          key,
          text:
            this.columnList.find((column) => column.key === key)?.text || '',
        } as IColumnOrderItem;
      }),
      'key'
    );

    this.columnOrder = newOrder;
  }
}
